/**
 * buildMovimientoTicketPlainText.ts — Texto plano canónico del comprobante de movimiento.
 *
 * Una sola tubería para vista térmica (58/80 mm) e impresión ESC/POS; la vista web
 * usa el mismo modelo vía `buildMovimientoTicketModel`.
 */

import type { Credito, MovimientoCaja, Venta } from "../types/caja.types";
import { MOVIMIENTO_TICKET_FOOTER } from "../constants/movimientoTicketCopy";
import {
  buildMovimientoTicketModel,
  type MovimientoTicketModel,
} from "./buildMovimientoTicketModel";
import { loadBusinessForReceipt } from "./saleTicketEscPos";
import {
  emitSepLine,
  getTicketMaxWidth,
  sanitizeForThermal,
} from "./escposLayout";

export interface BuildMovimientoTicketPlainTextInput {
  movimiento: MovimientoCaja;
  ventas?: Venta[];
  creditos?: Credito[];
  paperWidthMm: 58 | 80;
}

function money(n: number): string {
  return `$${Math.round(n).toLocaleString("es-CO")}`;
}

function rowTwoCol(label: string, value: string, maxW: number): string[] {
  const L = label.trimEnd();
  const R = value.trim();
  if (L.length + R.length + 1 <= maxW) {
    return [L + " ".repeat(maxW - L.length - R.length) + R];
  }
  return [L.slice(0, maxW), " ".repeat(Math.max(0, maxW - R.length)) + R];
}

function wrapParagraph(text: string, maxW: number): string[] {
  const words = text.replace(/\s+/g, " ").trim().split(" ");
  if (!words[0]) return [];
  const out: string[] = [];
  let line = "";
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (next.length <= maxW) {
      line = next;
    } else {
      if (line) out.push(line);
      line = w.length > maxW ? w.slice(0, maxW) : w;
    }
  }
  if (line) out.push(line);
  return out;
}

function pushBanner(lines: string[], text: string): void {
  lines.push(`<< ${text} >>`);
}

function pushBusinessHeader(lines: string[], maxW: number): void {
  const biz = loadBusinessForReceipt();
  lines.push(biz.nombre.toUpperCase());
  if (biz.slogan?.trim()) {
    lines.push(biz.slogan.trim());
  }
  if (biz.direccion?.trim()) {
    lines.push(...wrapParagraph(biz.direccion.trim(), maxW));
  }
  if (biz.nit?.trim()) {
    lines.push(`NIT: ${biz.nit.trim()}`);
  }
}

function pushDetalleRows(
  lines: string[],
  model: MovimientoTicketModel,
  maxW: number,
): void {
  lines.push(...rowTwoCol("Tipo:", model.tipoLinea, maxW));
  if (model.esAbonoCredito && model.clienteAbono) {
    lines.push(...rowTwoCol("Cliente:", model.clienteAbono, maxW));
  }
  if (model.metodoPago) {
    lines.push(...rowTwoCol("Medio:", model.metodoPago, maxW));
  }
  lines.push(...rowTwoCol("Fecha:", model.fechaDetalle, maxW));
  lines.push(...rowTwoCol("Hora:", model.horaDetalle, maxW));
  if (
    model.movimiento.tipo !== "anulacion" &&
    model.movimiento.tipo !== "devolucion"
  ) {
    lines.push(...rowTwoCol("Registrado por:", model.registradoPor, maxW));
  }
  if (model.autorizacion) {
    lines.push(...rowTwoCol("Autorizacion:", model.autorizacion, maxW));
  }
  if (model.soporte) {
    lines.push(...rowTwoCol("Soporte:", model.soporte, maxW));
  }
  if (model.caja) {
    lines.push(...rowTwoCol("Caja:", model.caja, maxW));
  }
}

function pushDevolucionBloque(
  lines: string[],
  model: MovimientoTicketModel,
  maxW: number,
): void {
  if (!model.devolucionRef) return;
  lines.push("");
  lines.push("REFERENCIA DEVOLUCION");
  lines.push(model.devolucionRef.id);
  for (const ln of wrapParagraph(model.devolucionRef.motivo, maxW)) {
    lines.push(ln);
  }
  if (model.devolucionNota) {
    for (const ln of wrapParagraph(model.devolucionNota, maxW)) {
      lines.push(ln);
    }
  }
  if (model.productosDevueltos.length > 0) {
    lines.push("");
    lines.push("PRODUCTOS DEVUELTOS");
    for (const p of model.productosDevueltos) {
      lines.push(...rowTwoCol(p.nombre, money(p.total), maxW));
    }
    if (model.stockRestauradoNota) {
      for (const ln of wrapParagraph(model.stockRestauradoNota, maxW)) {
        lines.push(ln);
      }
    }
  }
}

function pushAnulacionBloque(
  lines: string[],
  model: MovimientoTicketModel,
  maxW: number,
): void {
  if (model.anulacionProductos.length === 0 && !model.anulacionNota) return;
  if (model.anulacionProductos.length > 0) {
    lines.push("");
    lines.push("PRODUCTOS ANULADOS");
    for (const p of model.anulacionProductos) {
      lines.push(
        ...rowTwoCol(`- ${p.nombre}: ${p.cantidad} ud.`, money(p.total), maxW),
      );
    }
  }
  if (model.anulacionNota) {
    for (const ln of wrapParagraph(model.anulacionNota, maxW)) {
      lines.push(ln);
    }
  }
}

function pushCreditoBloque(
  lines: string[],
  model: MovimientoTicketModel,
  maxW: number,
): void {
  if (!model.creditoRef) return;
  lines.push("");
  lines.push("REFERENCIA CREDITO");
  lines.push(`Venta a crédito · ${model.creditoRef.ventaLabel}`);
  lines.push(
    ...rowTwoCol(
      "Valor de la venta:",
      money(model.creditoRef.valorVenta),
      maxW,
    ),
  );
  lines.push(
    ...rowTwoCol(
      "Abonado en este mov.:",
      money(model.creditoRef.abonadoMovimiento),
      maxW,
    ),
  );
  lines.push(
    ...rowTwoCol(
      "Saldo pendiente:",
      money(model.creditoRef.saldoPendiente),
      maxW,
    ),
  );
  lines.push(...rowTwoCol("Estado:", model.creditoRef.estado, maxW));
}

export function buildMovimientoTicketPlainText(
  input: BuildMovimientoTicketPlainTextInput,
): string[] {
  const maxW = getTicketMaxWidth(input.paperWidthMm);
  const model = buildMovimientoTicketModel({
    movimiento: input.movimiento,
    ventas: input.ventas,
    creditos: input.creditos,
  });
  const lines: string[] = [];

  if (model.bannerReversalText) {
    pushBanner(lines, model.bannerReversalText);
  }

  pushBusinessHeader(lines, maxW);
  lines.push(emitSepLine("_", maxW));

  lines.push(model.titulo.toUpperCase());
  lines.push(emitSepLine("_", maxW));

  lines.push("MONTO");
  lines.push(
    model.movimiento.tipo === "abrir_caja"
      ? "$$MONTO$$APERTURA DE GAVETA"
      : `$$MONTO$$${money(model.monto)}`,
  );
  lines.push(emitSepLine("_", maxW));

  pushDetalleRows(lines, model, maxW);
  lines.push(emitSepLine("_", maxW));

  pushDevolucionBloque(lines, model, maxW);
  pushAnulacionBloque(lines, model, maxW);
  pushCreditoBloque(lines, model, maxW);

  lines.push("");
  lines.push("MOTIVO / DETALLE");
  lines.push("");
  for (const ln of wrapParagraph(model.motivo, maxW)) {
    lines.push(ln);
  }
  lines.push("");
  lines.push(emitSepLine("_", maxW));

  lines.push(`ID: ${model.idCorto}`);
  lines.push(emitSepLine("_", maxW));

  lines.push(MOVIMIENTO_TICKET_FOOTER.gestionadoCon);
  lines.push(MOVIMIENTO_TICKET_FOOTER.marca);
  for (const ln of wrapParagraph(MOVIMIENTO_TICKET_FOOTER.avisoLegal, maxW)) {
    lines.push(ln);
  }

  return lines.map((ln) => sanitizeForThermal(ln));
}

/** Negrita para encode ESC/POS del movimiento. */
export function shouldBoldMovimientoTicketLine(
  line: string,
  section: "header" | "body" | "footer",
  headerTextLineIndex: number,
): boolean {
  const t = line.trim().replace(/^\$\$MONTO\$\$/, "");
  if (line.trim().startsWith("$$MONTO$$") || /^\$\d/.test(t)) {
    return section === "header";
  }
  if (t.startsWith("<<") && t.endsWith(">>")) return true;
  if (section === "header" && headerTextLineIndex <= 2 && t.length > 0) {
    return true;
  }
  if (/^COMPROBANTE DE/i.test(t)) return true;
  if (/^MOTIVO \/ DETALLE$/i.test(t)) return true;
  if (/^REFERENCIA|^PRODUCTOS|^ID:/i.test(t)) return true;
  return false;
}

export function shouldDoubleMovimientoTicketLine(line: string): boolean {
  return line.trim().startsWith("$$MONTO$$");
}

export function stripMovimientoTicketLineMarkers(line: string): string {
  return line.replace(/^\$\$MONTO\$\$/, "").trimEnd();
}
